export const STATUS_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> =
  {
    approved: 'default',
    denied: 'destructive',
    pending: 'outline',
    cancelled: 'secondary',
    modified: 'secondary',
  };

/**
 * Allowed urgency values. Single source of truth — the FilterBar options
 * array, the URL→FilterState validator, and any future enum-style guards
 * derive from this list. Keep ordered by severity descending so the UI
 * picker shows "stat" first.
 */
export const URGENCY_VALUES = ['stat', 'urgent', 'routine'] as const;
export type Urgency = (typeof URGENCY_VALUES)[number];

/**
 * Badge variant for the human-review workflow state — separate concept
 * from `status` (which is the payor's say). Mirrors the values written
 * by the review-action endpoint.
 */
export const REVIEW_VARIANTS: Record<string, 'default' | 'secondary' | 'outline'> = {
  draft: 'outline',
  reviewed: 'secondary',
  approved: 'default',
};

export type SectionKey =
  | 'authorization'
  | 'patient'
  | 'referringProvider'
  | 'referringPractice'
  | 'pcp'
  | 'renderingProvider'
  | 'renderingPractice'
  | 'insurance'
  | 'datesOfService'
  | 'requestId'
  | 'denialId'
  | 'diagnosisCodes'
  | 'serviceCodes'
  | 'notes';

/**
 * Which sections to show for each document classification.
 * Sections NOT listed are strictly hidden.
 */
export const CLASSIFICATION_VISIBLE_SECTIONS: Record<string, SectionKey[]> = {
  referral: [
    'authorization',
    'patient',
    'referringProvider',
    'referringPractice',
    'pcp',
    'renderingProvider',
    'renderingPractice',
    'insurance',
    'diagnosisCodes',
    'serviceCodes',
    'notes',
  ],
  denial: ['patient', 'insurance', 'datesOfService', 'denialId', 'notes'],
  medical_record: ['patient', 'referringProvider'],
  radiology_report: ['patient', 'referringProvider', 'renderingProvider', 'renderingPractice'],
  lab_result: ['patient', 'referringProvider'],
  prescription: ['patient', 'renderingProvider'],
  insurance_document: ['patient', 'insurance', 'datesOfService', 'requestId', 'notes'],
  release_form: ['patient', 'notes'],
  physician_letter: ['patient', 'renderingProvider', 'renderingPractice', 'notes'],
};

/** Returns whether a section should be visible given a classification. */
export function isSectionVisible(
  section: SectionKey,
  classification: string | null | undefined
): boolean {
  // Unknown or missing classification → show all sections
  if (!classification || !(classification in CLASSIFICATION_VISIBLE_SECTIONS)) {
    return true;
  }

  return CLASSIFICATION_VISIBLE_SECTIONS[classification]!.includes(section);
}
